import { useQuery } from '@tanstack/react-query';
import { useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import BackButton from '@/components/BackButton';
import ContentSpinner from '@/components/ContentSpinner';
import { PageHeading, TeamHeading } from '@/components/Layout/Parts';
import useAlerts from '@/hooks/useAlerts';
import useDidMount from '@/hooks/useDidMount';
import { SubtitlesGenerationProject } from '@/types/resources';
import { api } from '@/utils/api';
import { SubsGenerationProjectVideoStatus } from '@/utils/enums';

import MainInfoCard from './components/MainInfoCard';
import PreviewCard from './components/PreviewCard';
import StylesCard from './components/StylesCard';
import SubtitlesEditorCard from './components/SubtitlesEditorCard';

const Grid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 48px;
`;

const SubtitlesGenerationProjectPage = () => {
  const { projectId } = useParams();
  const alerts = useAlerts();
  const navigate = useNavigate();
  const previewCardRef = useRef<HTMLDivElement>(null);

  const { data: project, isLoading } = useQuery(
    [
      'subtitles_generation_projects',
      {
        id: projectId,
      },
    ],
    () =>
      api.get<never, SubtitlesGenerationProject>(
        `/v1/distribution/subtitles-generation-projects/${projectId}`,
      ),
    {
      retry: 2,
      refetchInterval: 45_000,
      onError: (err) => {
        if ((err as any)?.response?.status === 404) {
          return navigate('/404', {
            replace: true,
          });
        }
        alerts.error();
      },
    },
  );

  useDidMount(() => {
    if (
      [
        SubsGenerationProjectVideoStatus.GENERATING,
        SubsGenerationProjectVideoStatus.DONE,
      ].includes(
        project?.videoStatus ?? SubsGenerationProjectVideoStatus.NOT_READY,
      )
    ) {
      previewCardRef.current?.scrollIntoView({
        block: 'center',
      });
    }
  });

  if (isLoading || !project) {
    return <ContentSpinner />;
  }

  return (
    <>
      <TeamHeading />
      <PageHeading>{project.title ?? 'No Title'}</PageHeading>
      <BackButton />
      <Grid>
        <MainInfoCard project={project} />
        <SubtitlesEditorCard project={project} />
        <StylesCard project={project} />
        <PreviewCard project={project} ref={previewCardRef} />
      </Grid>
    </>
  );
};

export default SubtitlesGenerationProjectPage;
